import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Users, X, Share2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { projectAPI, authAPI, createQueryKey } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { getImageUrl } from '../config/runtime';

/**
 * InviteTeamCard - Unified component for team management and invitations
 * 
 * Shows in chat for:
 * - Single-player projects: Allows converting to multiplayer by inviting people
 * - Multiplayer projects: Shows team members and allows inviting more
 * - Site projects: Always shows (they're collaborative by nature)
 * 
 * Features:
 * - Invite by email or username search
 * - View current team members
 * - Convert single-player to multiplayer
 * - Access control (public/private) for owners
 */
const InviteTeamCard = ({ project, projectId, isOwner, onTeamUpdate }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Determine if this is a site project
  const isSiteProject = project?.settings?.archetype === 'site';
  
  // Fetch participants
  const { data: participantsResp, isLoading: isLoadingParticipants } = useQuery({
    queryKey: ['participants', projectId],
    queryFn: () => projectId ? projectAPI.getParticipants(projectId) : Promise.resolve({ data: { data: { participants: [] } } }),
    enabled: !!projectId,
    staleTime: 60 * 1000
  });
  const participants = participantsResp?.data?.data?.participants || [];
  
  // Combine owner and participants for member list
  const allMembers = useMemo(() => {
    const members = [project?.owner, ...participants].filter(Boolean);
    const membersMap = new Map();
    members.forEach(member => {
      if (member?.id && !membersMap.has(member.id)) {
        membersMap.set(member.id, member);
      }
    });
    return Array.from(membersMap.values());
  }, [project?.owner, participants]);
  
  // User search query
  const { data: userSearchResults, isLoading: isSearchingUsers, error: userSearchError } = useQuery({
    queryKey: ['user-search', userSearchQuery],
    queryFn: () => authAPI.searchUsers(userSearchQuery, 10),
    enabled: userSearchQuery.length >= 2,
    staleTime: 30000
  });
  
  const searchUsers = userSearchResults?.data?.data?.users || [];
  
  // Add collaborator mutation
  const addCollaboratorMutation = useMutation({
    mutationFn: ({ email, userId }) => {
      const payload = { role: 'VIEWER' };
      if (userId) {
        payload.userId = userId;
      } else if (email) {
        payload.email = email;
      }
      return projectAPI.addCollaborator(projectId, payload);
    },
    onSuccess: () => {
      // toast.success('Member added successfully');
      setUserSearchQuery('');
      setSelectedUser(null);
      setShowInviteModal(false);
      queryClient.invalidateQueries(['participants', projectId]);
      queryClient.invalidateQueries(createQueryKey.project(projectId));
      // Callback to notify parent of team update
      if (onTeamUpdate) {
        onTeamUpdate();
      }
    },
    onError: (error) => {
      let errorMessage = 'Failed to add member';
      if (error?.response?.data) {
        const data = error.response.data;
        if (Array.isArray(data.detail) && data.detail.length > 0) {
          const firstError = data.detail[0];
          errorMessage = firstError?.msg || firstError?.message || firstError?.type || errorMessage;
          if (typeof errorMessage === 'object') {
            errorMessage = JSON.stringify(errorMessage);
          }
        } else if (typeof data.detail === 'string') {
          errorMessage = data.detail;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    }
  });
  
  // Update project mutation (for access control)
  const updateProjectMutation = useMutation({
    mutationFn: (data) => projectAPI.update(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(createQueryKey.project(projectId));
    },
    onError: () => {
      toast.error('Failed to update project settings');
    }
  });
  
  // Share handler
  const handleShare = async () => {
    const url = window.location.href;
    const title = project?.name || 'Project';
    const text = `Join me on ${title}`;
    
    const isMobile = window.matchMedia('(pointer: coarse)').matches;
    if (isMobile && navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch (err) {
        if (err.name === 'AbortError') return;
      }
    }
    
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      toast.error('Copy failed');
    }
  };
  
  // Determine if we should show this card
  // Show for: ongoing projects where user is owner OR (multiplayer OR site project)
  const shouldShow = project?.status === 'ongoing' && (
    isOwner || 
    project?.isMultiplayer || 
    isSiteProject
  );
  
  if (!shouldShow) {
    return null;
  }
  
  const isMultiplayer = project?.isMultiplayer || allMembers.length > 1;
  const canInvite = isOwner;
  
  return (
    <>
      {/* Team/Invite Card - Compact, integrated design */}
      <div className="mb-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden">
        <div className="flex min-h-[56px]">
          {/* Icon section - unified green color */}
          <div className="relative w-16 min-h-full overflow-hidden flex-shrink-0">
            <div className="absolute inset-0 w-full h-full bg-green-500 dark:bg-green-600 rounded-l-2xl flex items-center justify-center">
              <Users size={20} className="text-white" />
            </div>
          </div>
          
          {/* Content section */}
          <div className="flex-1 p-5 flex items-center min-w-0">
            <div className="flex items-center justify-between w-full gap-3">
              <div className="flex-1 min-w-0">
                {/* Title with member count */}
                <div className="flex items-center gap-2">
                  <span className="text-[14px] font-semibold text-gray-900 dark:text-white">
                    {isMultiplayer ? 'Team' : 'Solo Project'}
                  </span>
                  {isMultiplayer && (
                    <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                      {allMembers.length}
                    </span>
                  )}
                </div>
                
                {/* Team members preview */}
                {isMultiplayer && allMembers.length > 0 && (
                  <div className="flex items-center gap-2 mt-1.5">
                    <div className="inline-flex items-center -space-x-1.5">
                      {allMembers.slice(0, 4).map((member, idx) => {
                        const memberInitials = member.firstName && member.lastName
                          ? `${member.firstName[0]}${member.lastName[0]}`.toUpperCase()
                          : member.username?.[0]?.toUpperCase() || 'U';
                        const avatarUrl = member?.avatar && typeof member.avatar === 'string' && member.avatar.trim() 
                          ? getImageUrl(member.avatar) 
                          : null;
                        
                        return (
                          <div
                            key={member.id || idx}
                            className="relative inline-flex items-center justify-center w-6 h-6 rounded-full border-2 border-white dark:border-gray-800 bg-gradient-to-br from-green-500 to-purple-600 text-white text-[9px] font-bold overflow-hidden"
                            title={`@${member.username || 'Unknown'}`}
                          >
                            <span className="absolute inset-0 flex items-center justify-center">{memberInitials}</span>
                            {avatarUrl && (
                              <img 
                                src={avatarUrl} 
                                alt="" 
                                className="absolute inset-0 w-full h-full rounded-full object-cover"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            )}
                          </div>
                        );
                      })}
                      {allMembers.length > 4 && (
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full border-2 border-white dark:border-gray-800 bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300 text-[9px] font-bold">
                          +{allMembers.length - 4}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/projects/${projectId}`, { state: { tab: 'members' } }); }}
                      className="text-[11px] text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 font-medium"
                    >
                      View all →
                    </button>
                  </div>
                )}
              </div>
              
              {/* Action buttons */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={handleShare}
                  className="p-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-lg transition-colors"
                  title="Share project link"
                >
                  <Share2 className="w-4 h-4" />
                </button>
                
                {canInvite && (
                  <button
                    onClick={() => setShowInviteModal(true)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-green-500 hover:bg-green-600 text-white text-[13px] font-semibold rounded-lg transition-colors"
                  >
                    <UserPlus className="w-4 h-4" />
                    <span className="hidden sm:inline">Invite</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/50 p-4" onClick={(e) => {
          if (e.target === e.currentTarget) {
            setShowInviteModal(false);
            setUserSearchQuery('');
            setSelectedUser(null);
          }
        }}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6 relative">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Invite Member
              </h3>
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setUserSearchQuery('');
                  setSelectedUser(null);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Selected User Display */}
              {selectedUser && (
                <div className="p-4 bg-green-50 dark:bg-green-900/20 border-2 border-green-500 dark:border-green-400 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-600 to-purple-600 text-white flex items-center justify-center font-bold text-sm">
                        {selectedUser.firstName?.[0] || selectedUser.username?.[0] || 'U'}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 dark:text-white">
                          {selectedUser.firstName && selectedUser.lastName
                            ? `${selectedUser.firstName} ${selectedUser.lastName}`
                            : selectedUser.username || 'Unknown'}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">@{selectedUser.username} • Selected</div>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedUser(null)}
                      className="p-1.5 hover:bg-green-100 dark:hover:bg-green-900/40 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    </button>
                  </div>
                </div>
              )}

              {/* Search Input - Only show when no user selected */}
              {!selectedUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Search Users
                  </label>
                  <input
                    type="text"
                    value={userSearchQuery}
                    onChange={(e) => {
                      setUserSearchQuery(e.target.value);
                      setSelectedUser(null);
                    }}
                    placeholder="Search by username or name..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  
                  {/* Only show searching when actually searching and no results yet */}
                  {isSearchingUsers && userSearchQuery.length >= 2 && searchUsers.length === 0 && (
                    <div className="mt-2 text-sm text-gray-500 dark:text-gray-400 text-center py-2 flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                      <span>Searching...</span>
                    </div>
                  )}
                  
                  {userSearchError && !isSearchingUsers && (
                    <div className="mt-2 text-sm text-red-500 text-center py-2">
                      Failed to search users
                    </div>
                  )}
                  
                  {/* User Search Results */}
                  {!isSearchingUsers && userSearchQuery.length >= 2 && searchUsers.length > 0 && (
                    <div className="mt-2 border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 shadow-lg max-h-60 overflow-y-auto">
                      {searchUsers.map((searchUser) => {
                        const isAlreadyMember = allMembers.some(m => m.id === searchUser.id);
                        const isCurrentUser = user && searchUser.id === user.id;
                        if (isAlreadyMember || isCurrentUser) return null;
                        
                        return (
                          <button
                            key={searchUser.id}
                            onClick={() => setSelectedUser(searchUser)}
                            className="w-full px-4 py-3 hover:bg-green-50 dark:hover:bg-green-900/20 flex items-center gap-3 text-left transition-colors"
                          >
                            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-600 to-purple-600 flex items-center justify-center text-white font-semibold">
                              {searchUser.firstName?.[0] || searchUser.username?.[0] || 'U'}
                            </div>
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 dark:text-white">
                                {searchUser.firstName && searchUser.lastName
                                  ? `${searchUser.firstName} ${searchUser.lastName}`
                                  : searchUser.username || 'Unknown'}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                @{searchUser.username}
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* No results message */}
                  {!isSearchingUsers && userSearchQuery.length >= 2 && searchUsers.length === 0 && !userSearchError && (
                    <div className="mt-2 text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                      No users found
                    </div>
                  )}
                </div>
              )}
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowInviteModal(false);
                    setUserSearchQuery('');
                    setSelectedUser(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (selectedUser) addCollaboratorMutation.mutate({ userId: selectedUser.id });
                  }}
                  disabled={!selectedUser || addCollaboratorMutation.isPending}
                  className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addCollaboratorMutation.isPending ? 'Adding...' : 'Add Member'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InviteTeamCard;

